import { useState, useEffect } from "react";
import { C } from "../../lib/colors";
import { useT } from "../LangContext";
import { openGuideDrawer } from "../../lib/guideDrawer";

// ─── Fase 15-16 · Bloque 3 — TabCoach (contextual guide banner) ──────────
// Translucent-linen banner above each tab body. Shows purpose + 2-3 numbered
// micro-steps + a CTA to the next step. Dismissible per tab, persisted in
// localStorage as `pf_coach__dismissed` (object of {[tabId]: true}). A small
// "show guide" link re-opens.
const STORAGE_KEY = "pf_coach__dismissed";

function loadDismissed() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
}
function saveDismissed(d) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch { /* noop */ }
}

export default function TabCoach({ tabId, onCta, ctaDisabled }) {
  const { t } = useT();
  const [dismissed, setDismissed] = useState(() => loadDismissed()[tabId] === true);

  // Re-sync if tab changes
  useEffect(() => { setDismissed(loadDismissed()[tabId] === true); }, [tabId]);

  const purpose = t(`coach__${tabId}__purpose`);
  // Steps come as a JSON array string from i18n (interpolation can't return arrays);
  // we parse it here.
  let steps = [];
  try {
    const raw = t(`coach__${tabId}__steps`);
    steps = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch { steps = []; }
  const cta = t(`coach__${tabId}__cta`);

  if (dismissed) {
    return (
      <div data-testid={`coach-collapsed-${tabId}`}
           style={{ textAlign: "right", marginBottom: 8 }}>
        <button onClick={() => {
                  const d = loadDismissed(); delete d[tabId]; saveDismissed(d);
                  setDismissed(false);
                }}
                data-testid={`coach-show-${tabId}`}
                style={{ background: "none", border: "none", color: C.smoke,
                          fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: 0.18,
                          cursor: "pointer", textDecoration: "underline dotted", padding: 0 }}>
          {t("coach__show")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`coach-${tabId}`}
         style={{ marginBottom: 16, padding: "12px 16px",
                   border: `1px solid ${C.bone}33`,
                   background: "rgba(216, 213, 199, 0.05)",
                   color: C.linen, fontFamily: "var(--plex)", fontSize: 13,
                   lineHeight: 1.55, position: "relative" }}>
      <button data-testid={`coach-dismiss-${tabId}`}
              onClick={() => { const d = loadDismissed(); d[tabId] = true; saveDismissed(d); setDismissed(true); }}
              title={t("coach__dismiss")}
              style={{ position: "absolute", top: 8, right: 10, background: "none",
                        border: "none", color: C.smoke, cursor: "pointer", fontSize: 14,
                        lineHeight: 1, padding: 4 }}>
        ✕
      </button>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, paddingRight: 24 }}>
        <span style={{ color: C.brass, fontSize: 14, flexShrink: 0, marginTop: 1 }}>ⓘ</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: C.bone, marginBottom: 6 }}>{purpose}</div>
          {steps.length > 0 && (
            <ol style={{ margin: "4px 0 8px", paddingLeft: 22, color: C.linen }}>
              {steps.map((s, i) => (
                <li key={i} style={{ marginBottom: 3 }}>{s}</li>
              ))}
            </ol>
          )}
          {cta && cta !== `coach__${tabId}__cta` && (
            <button data-testid={`coach-cta-${tabId}`}
                    onClick={onCta}
                    disabled={ctaDisabled}
                    style={{ background: "transparent",
                              border: `1px solid ${ctaDisabled ? C.dust : C.brass}`,
                              color: ctaDisabled ? C.smoke : C.brass,
                              padding: "5px 12px", marginTop: 4,
                              cursor: ctaDisabled ? "not-allowed" : "pointer",
                              fontFamily: "var(--mono)", fontSize: 11, letterSpacing: 0.18,
                              opacity: ctaDisabled ? 0.5 : 1 }}>
              {cta}
            </button>
          )}
          {/* Fase 17B · Bloque 2 — Persistent link to the full guide drawer */}
          <div style={{ marginTop: 10, paddingTop: 8,
                          borderTop: `1px dashed ${C.dust}` }}>
            <button data-testid={`coach-open-guide-${tabId}`}
                    onClick={openGuideDrawer}
                    style={{ background: "transparent", border: "none", padding: 0,
                              color: C.brass, fontFamily: "var(--plex)",
                              fontSize: 12, cursor: "pointer",
                              textDecoration: "underline dotted",
                              letterSpacing: 0.05 }}>
              {t("guide_open_full")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
